import { ChevronRight } from "lucide-react";
import { type ReactElement, type ReactNode, useState } from "react";

import { cn } from "@/lib/utils";

/**
 * One block of a page: a heading, an optional line of explanation and, where it saves
 * something, one action row at its foot. No frame of its own (D43): the page is one white card,
 * and a card in it was a card in a card. Space and the heading set the blocks apart.
 */
export function Section({
  title,
  description,
  actions,
  footer,
  tone = "default",
  id,
  className,
  children,
}: {
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  /** Buttons at the right of the heading, e.g. "Sponsor anlegen". */
  readonly actions?: ReactNode;
  /** The one save row of the card. */
  readonly footer?: ReactNode;
  /** `danger` frames final steps: archive, end, shut access. */
  readonly tone?: "default" | "danger";
  readonly id?: string;
  readonly className?: string;
  readonly children?: ReactNode;
}): ReactElement {
  return (
    <section
      id={id}
      className={cn(
        "mb-10 last:mb-0",
        tone === "danger" ? "border-t border-destructive/30 pt-6" : null,
        className,
      )}
    >
      {title !== undefined || actions !== undefined ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            {title !== undefined ? (
              <h2
                className={cn(
                  "text-xl font-bold",
                  tone === "danger" ? "text-destructive" : "text-foreground",
                )}
              >
                {title}
              </h2>
            ) : null}
            {description !== undefined ? (
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{description}</p>
            ) : null}
          </div>
          {actions !== undefined ? <div className="flex shrink-0 gap-2">{actions}</div> : null}
        </div>
      ) : null}
      <div className={title !== undefined || actions !== undefined ? "mt-4" : undefined}>
        {children}
      </div>
      {footer !== undefined ? <div className="mt-4 flex gap-2">{footer}</div> : null}
    </section>
  );
}

/**
 * Settings that are rarely needed, folded away under one line (D43). A native disclosure: the
 * fields stay in the form while it is closed, so the save bar still reads and checks them, and an
 * invalid one is opened by the bar before the browser points at it (`reportValidityAboveBar`).
 * One level only, never one inside another.
 */
export function Advanced({
  summary,
  open = false,
  className,
  children,
}: {
  readonly summary: string;
  /**
   * Open from the start, e.g. where the action already uses its own values. Read once: later
   * only the person (and the save bar) open and close it, or typing could fold it away.
   */
  readonly open?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}): ReactElement {
  const [initiallyOpen] = useState(open);
  return (
    <details className={cn("group mt-4", className)} open={initiallyOpen}>
      <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 text-sm font-medium [&::-webkit-details-marker]:hidden">
        <ChevronRight
          aria-hidden="true"
          className="size-4 transition-transform group-open:rotate-90 motion-reduce:transition-none"
        />
        {summary}
      </summary>
      <div className="pb-2 pl-6">{children}</div>
    </details>
  );
}
